/**
 * useBulkDownloadSpecs — orchestrates bulk tech-spec ZIP download.
 *
 * Flow:
 * 1. start(unitIds) → POSTs manifest via auth.callWithAuth → sets pendingManifest
 * 2. Consumer renders confirm dialog; user clicks Confirm
 * 3. confirmDownload() → registers beforeunload, streams ZIP to disk via
 *    showSaveFilePicker (Chrome/Edge) or StreamSaver.js fallback (Firefox/Safari)
 * 4. ZIP entries: {grwId}/{filename}; duplicate filenames auto-suffixed (name (2).ext)
 * 5. manifest.json at ZIP root — one record per unit incl. units with no attachments
 * 6. Each R2 fetch wrapped with AbortSignal.timeout(60 s); retried 2× on failure
 * 7. Skipped filenames surfaced as toast.error at the end
 *
 * Covers REQ-9.4.
 */
import { useState, useRef, useCallback } from 'react';
import { useRouteContext } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { mediaUnitAttachments } from '@ooh/contracts';
import type { BulkDownloadManifestResponse } from '@ooh/contracts';
import { toast } from 'sonner';

// showSaveFilePicker is not yet part of TypeScript's standard DOM lib types.
// Augment Window once here so call sites stay type-safe without inline casts.
declare global {
  interface Window {
    showSaveFilePicker?: (options: {
      suggestedName?: string;
      types?: { description: string; accept: Record<string, string[]> }[];
    }) => Promise<FileSystemFileHandle>;
  }
}

// ── Constants ─────────────────────────────────────────────────────────────────

const MAX_RETRIES = 2; // 3 total attempts (initial + 2 retries)
const FETCH_TIMEOUT_MS = 60_000;
const ZIP_FILENAME = 'specs.zip';

// ── Feature detection ─────────────────────────────────────────────────────────

/**
 * Returns the File System Access API's showSaveFilePicker when it is available
 * and not blocked by an iframe cross-origin context, otherwise null.
 * Returning the typed function (rather than a boolean) lets call sites invoke it
 * without re-narrowing or casting `window`.
 * See: https://developer.mozilla.org/en-US/docs/Web/API/Window/showSaveFilePicker
 */
function getNativeFileSavePicker(): NonNullable<Window['showSaveFilePicker']> | null {
  if (!window.showSaveFilePicker) return null;
  try {
    return window.self === window.top ? window.showSaveFilePicker : null;
  } catch {
    // Cross-origin iframe — access to window.top throws
    return null;
  }
}

// ── Duplicate-filename deduplication ─────────────────────────────────────────

/**
 * Given a base filename and a seen-names map, returns a unique filename by
 * appending ` (N)` before the extension when a collision is detected.
 * Mutates the map to track the new name.
 *
 * Examples:
 *   spec.pdf → spec.pdf (first occurrence)
 *   spec.pdf → spec (2).pdf (second occurrence)
 *   spec.pdf → spec (3).pdf (third occurrence)
 */
function uniqueFilename(base: string, seen: Map<string, number>): string {
  if (!seen.has(base)) {
    seen.set(base, 1);
    return base;
  }
  const count = seen.get(base)! + 1;
  seen.set(base, count);

  // Split on last '.' to handle filenames with multiple dots
  const lastDot = base.lastIndexOf('.');
  if (lastDot === -1) {
    return `${base} (${count})`;
  }
  const name = base.slice(0, lastDot);
  const ext = base.slice(lastDot); // includes the dot
  return `${name} (${count})${ext}`;
}

// ── Retry-wrapped R2 fetch ────────────────────────────────────────────────────

/**
 * Fetches a presigned R2 URL with a 60-second timeout, retrying up to MAX_RETRIES
 * times on failure. R2 presigned URLs are fetched directly from the browser —
 * this is intentional per the architecture (bytes do not flow through Railway).
 */
async function fetchWithRetry(url: string): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      // R2 presigned URLs — raw fetch is permitted here; bytes never touch Railway
      // eslint-disable-next-line no-restricted-globals
      const res = await fetch(url, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) }); // eslint-disable-line no-await-in-loop
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res;
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
}

// ── ZIP entry and manifest builders ──────────────────────────────────────────

interface ManifestRecord {
  grwId: string;
  unitId: number;
  city?: string;
  address?: string;
  files: string[];
}

function buildManifestRecords(manifest: BulkDownloadManifestResponse): ManifestRecord[] {
  const unitRecords: ManifestRecord[] = manifest.units.map((unit) => {
    const seenNames = new Map<string, number>();
    return {
      grwId: unit.grwId,
      unitId: unit.unitId,
      city: unit.city,
      address: unit.address,
      files: unit.attachments.map((a) => uniqueFilename(a.filename, seenNames)),
    };
  });

  const noAttachmentRecords: ManifestRecord[] = manifest.unitsWithoutAttachments.map((u) => ({
    grwId: u.grwId,
    unitId: u.unitId,
    // city and address are omitted — unitsWithoutAttachments only carry unitId + grwId
    files: [],
  }));

  return [...unitRecords, ...noAttachmentRecords];
}

/**
 * Fetches each attachment for a unit, applying duplicate-filename deduplication.
 * Returns fetched entries and appends any skipped filenames to the provided array.
 */
async function fetchUnitEntries(
  unit: BulkDownloadManifestResponse['units'][number],
  skippedFilenames: string[],
): Promise<Array<{ name: string; input: Response }>> {
  const seenNames = new Map<string, number>();
  const entries: Array<{ name: string; input: Response }> = [];
  for (const attachment of unit.attachments) {
    const entryName = uniqueFilename(attachment.filename, seenNames);
    try {
      const res = await fetchWithRetry(attachment.downloadUrl); // eslint-disable-line no-await-in-loop
      entries.push({ name: `${unit.grwId}/${entryName}`, input: res });
    } catch (err) {
      console.error('[bulk-download] file fetch failed', attachment.filename, err);
      skippedFilenames.push(attachment.filename);
    }
  }
  return entries;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface UseBulkDownloadSpecsResult {
  /** Start the flow: fetch manifest then surface confirm dialog. */
  start: (unitIds: number[]) => Promise<void>;
  /** Whether ZIP streaming is currently in progress. */
  isStreaming: boolean;
  /** The fetched manifest waiting for user confirmation; null otherwise. */
  pendingManifest: BulkDownloadManifestResponse | null;
  /** Trigger ZIP streaming after user confirms the dialog. */
  confirmDownload: () => Promise<void>;
  /** Abort the flow before streaming starts. */
  cancelDownload: () => void;
}

// eslint-disable-next-line max-lines-per-function -- orchestrates manifest fetch + streaming pipeline; splitting would obscure the flow
export function useBulkDownloadSpecs(): UseBulkDownloadSpecsResult {
  const { auth } = useRouteContext({ from: '__root__' });
  const { t } = useTranslation('inventory');

  const [isStreaming, setIsStreaming] = useState(false);
  const [pendingManifest, setPendingManifest] = useState<BulkDownloadManifestResponse | null>(null);

  // Stable ref holding the beforeunload handler so removal is guaranteed even
  // if the component re-renders between register and remove.
  const beforeunloadRef = useRef<((e: BeforeUnloadEvent) => void) | null>(null);

  // ── Cleanup helper ──────────────────────────────────────────────────────────

  const cleanupBeforeunload = useCallback(() => {
    if (beforeunloadRef.current) {
      window.removeEventListener('beforeunload', beforeunloadRef.current);
      beforeunloadRef.current = null;
    }
  }, []);

  // ── start() ────────────────────────────────────────────────────────────────

  const start = useCallback(
    async (unitIds: number[]): Promise<void> => {
      try {
        const manifest = await auth.callWithAuth(mediaUnitAttachments.bulkDownloadManifest, {
          unitIds,
        });
        // callWithAuth returns unknown — the response is Zod-validated server-side;
        // the cast is safe because the API contract guarantees this shape.
        setPendingManifest(manifest as BulkDownloadManifestResponse);
      } catch (err) {
        console.error('[bulk-download] manifest request failed', err);
        toast.error(t('bulkDownload.toast.manifestError'));
        setPendingManifest(null);
      }
    },
    [auth, t],
  );

  // ── confirmDownload() ──────────────────────────────────────────────────────

  // eslint-disable-next-line max-lines-per-function -- streaming pipeline with retry/skip logic
  const confirmDownload = useCallback(async (): Promise<void> => {
    if (!pendingManifest) return;

    // Register beforeunload guard — must happen before any async streaming work
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    beforeunloadRef.current = handler;
    window.addEventListener('beforeunload', handler);

    setIsStreaming(true);

    try {
      // Dynamically import heavy libraries (only loaded on first confirm)
      const [{ downloadZip }, streamsaverModule] = await Promise.all([
        import('client-zip'),
        import('streamsaver'),
      ]);
      const streamsaver = streamsaverModule.default;

      // Point StreamSaver at our same-origin mitm.html for Firefox/Safari
      streamsaver.mitm = '/mitm.html';

      // ── Build ZIP file entries ───────────────────────────────────────────

      const skippedFilenames: string[] = [];
      const fileEntries: Array<{ name: string; input: Response }> = [];

      for (const unit of pendingManifest.units) {
        const unitEntries = await fetchUnitEntries(unit, skippedFilenames); // eslint-disable-line no-await-in-loop
        fileEntries.push(...unitEntries);
      }

      // ── Build manifest.json ──────────────────────────────────────────────

      const manifestBlob = new Blob([JSON.stringify(buildManifestRecords(pendingManifest))], {
        type: 'application/json',
      });

      const allEntries = [...fileEntries, { name: 'manifest.json', input: manifestBlob }];

      // ── Stream to disk ───────────────────────────────────────────────────

      const zipResponse = downloadZip(allEntries);

      // Guard once — both branches need the body stream.
      // Use toast+return instead of throw so the void caller does not silently swallow a rejection.
      if (!zipResponse.body) {
        console.error('[bulk-download] ZIP stream body is null');
        toast.error(t('bulkDownload.toast.streamError'));
        return;
      }

      const savePicker = getNativeFileSavePicker();

      try {
        if (savePicker) {
          // Native File System Access API (Chrome / Edge)
          const fileHandle = await savePicker({
            suggestedName: ZIP_FILENAME,
            types: [{ description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } }],
          });
          const writable = await fileHandle.createWritable();
          // pipeTo() streams directly without buffering the full archive in memory;
          // it also closes the writable automatically on completion.
          await zipResponse.body.pipeTo(writable);
        } else {
          // StreamSaver.js fallback (Firefox / Safari)
          // pipeTo() handles error propagation and reader-lock cleanup natively
          await zipResponse.body.pipeTo(streamsaver.createWriteStream(ZIP_FILENAME));
        }
      } catch (err) {
        // AbortError = user dismissed the Save As dialog — silently ignore
        if (err instanceof DOMException && err.name === 'AbortError') return;
        console.error('[bulk-download] stream write failed', err);
        toast.error(t('bulkDownload.toast.streamError'));
        return;
      }

      // ── Post-stream cleanup ──────────────────────────────────────────────

      if (skippedFilenames.length > 0) {
        toast.error(
          t('bulkDownload.toast.skippedFiles', {
            count: skippedFilenames.length,
            files: skippedFilenames.join(', '),
          }),
        );
      }
    } finally {
      cleanupBeforeunload();
      setIsStreaming(false);
      setPendingManifest(null);
    }
  }, [pendingManifest, cleanupBeforeunload, t]);

  // ── cancelDownload() ───────────────────────────────────────────────────────

  const cancelDownload = useCallback(() => {
    setPendingManifest(null);
    // No beforeunload registered since streaming never started
  }, []);

  return {
    start,
    isStreaming,
    pendingManifest,
    confirmDownload,
    cancelDownload,
  };
}
